export const AGENT_SESSION_STORAGE_KEY = "timingChartAgentSessionRef";
export const AGENT_SESSION_REFERENCE_VERSION = 2;

export function deriveAgentJobStatus(status = "") {
    const value = String(status || "").toLowerCase();
    if (!value || value === "idle") return "queued";
    if (value === "asking" || value === "waiting_answer") return "waiting_user";
    if (value === "ready" || value === "applied") return "completed";
    if (value === "failed") return "failed";
    if (value === "cancelled") return "cancelled";
    return "running";
}

export function createEmptyAgentSession(now = new Date().toISOString()) {
    return {
        active: false,
        mode: "flow",
        status: "idle",
        jobStatus: "queued",
        jobType: "flow_plan",
        canPoll: false,
        isTerminalJobState: false,
        stateAuthority: "client",
        goal: "",
        sourceText: "",
        plan: [],
        currentStepIndex: 0,
        assumptions: [],
        activityLog: [],
        observations: [],
        questions: [],
        answers: {},
        nextActions: [],
        nextAction: "",
        memory: {
            rejectedIdeas: [],
            confirmedFacts: [],
            warnings: []
        },
        remoteSessionId: "",
        remoteStatus: "idle",
        remoteEtag: "",
        serverRevisionCount: 0,
        serverUpdatedAt: "",
        workspaceId: "",
        projectId: "",
        generationRunId: "",
        clientRequestId: "",
        generationRunPersisted: false,
        generatedCode: "",
        draft: null,
        deliveredAt: "",
        currentQuestionIndex: 0,
        revisionCount: 0,
        createdAt: now,
        updatedAt: now,
        localUpdatedAt: now
    };
}

export function assignChatAgentSession(chatState, session, helpers = {}) {
    if (!chatState) return session || null;
    const createEmptySession = helpers.createEmptyAgentSession || createEmptyAgentSession;
    const nextSession = session || createEmptySession();
    chatState.agentSession = nextSession;
    chatState.planSession = nextSession;
    return nextSession;
}

export function ensureChatAgentSession(chatState, helpers = {}) {
    if (!chatState) return null;
    if (!chatState.agentSession) {
        return assignChatAgentSession(chatState, null, helpers);
    }
    if (chatState.planSession !== chatState.agentSession) {
        chatState.planSession = chatState.agentSession;
    }
    return chatState.agentSession;
}

export function getCurrentPlanQuestion(session = null) {
    if (!session?.active) return null;
    return session.questions?.[session.currentQuestionIndex] || null;
}

export function buildChatInputPlaceholder(session = null, fallback = "追加指示を入力") {
    if (session?.active && ["waiting_answer", "asking"].includes(session.status)) {
        const question = getCurrentPlanQuestion(session);
        if (question?.kind === "text") {
            return question.placeholder || "回答を入力";
        }
        return "選択肢ボタンから回答できます";
    }
    return fallback;
}

export function buildAgentPlanSteps() {
    return [
        { id: "parse_goal", label: "入力を読み取る" },
        { id: "draft_flow", label: "初期フロー案を作る" },
        { id: "inspect_missing", label: "未確定条件を洗い出す" },
        { id: "ask_user", label: "確認質問を出す" },
        { id: "revise_draft", label: "回答を反映して調整する" },
        { id: "validate_draft", label: "最終チェックする" },
        { id: "ready_to_apply", label: "反映できる状態にする" }
    ];
}

export function buildInitialAgentSessionData(userMessage = "", helpers = {}) {
    const cloneJsonValue = helpers.cloneJsonValue || ((value) => JSON.parse(JSON.stringify(value)));
    const buildLocalFlowDraft = helpers.buildLocalFlowDraft || (() => ({ axes: [], nodes: [], connections: [] }));
    const extractAxisNameFromText = helpers.extractAxisNameFromText || (() => "");
    const extractTimeoutSecFromText = helpers.extractTimeoutSecFromText || (() => "");
    const extractErrorCodeFromText = helpers.extractErrorCodeFromText || (() => "");
    const detectOffBranchFromText = helpers.detectOffBranchFromText || (() => "");
    const detectAfterMotionFromText = helpers.detectAfterMotionFromText || (() => "");
    const applyPlanAnswerToDraft = helpers.applyPlanAnswerToDraft || (() => {});

    const draft = cloneJsonValue(buildLocalFlowDraft(userMessage));
    const assumptions = [];
    const presetAnswers = {};
    const plan = buildAgentPlanSteps().map((step, index) => ({ ...step, index }));

    const axisName = extractAxisNameFromText(userMessage);
    if (axisName) {
        presetAnswers.axis_name = axisName;
        assumptions.push(`使用軸は ${axisName} と仮定`);
    } else {
        assumptions.push("使用軸は確認質問で確定");
    }

    const timeoutSec = extractTimeoutSecFromText(userMessage);
    if (timeoutSec) {
        presetAnswers.timeout_sec = timeoutSec;
        assumptions.push(`タイムアウトは ${timeoutSec} 秒と仮定`);
    }

    const errorCode = extractErrorCodeFromText(userMessage);
    if (errorCode) {
        presetAnswers.error_code = errorCode;
        assumptions.push(`タイムアウト時エラーコードは ${errorCode}`);
    }

    const offBranch = detectOffBranchFromText(userMessage);
    if (offBranch) presetAnswers.off_branch = offBranch;

    const afterMotion = detectAfterMotionFromText(userMessage);
    if (afterMotion) presetAnswers.after_motion = afterMotion;

    for (const [key, value] of Object.entries(presetAnswers)) {
        applyPlanAnswerToDraft(draft, key, value);
    }

    return {
        draft,
        assumptions,
        presetAnswers,
        plan,
        nextActions: ["入力を読む", "初期フロー案を組み立てる"]
    };
}

export function createPlanningAgentSession(userMessage = "", helpers = {}) {
    const createEmptySession = helpers.createEmptyAgentSession || createEmptyAgentSession;
    const cloneJsonValue = helpers.cloneJsonValue || ((value) => JSON.parse(JSON.stringify(value)));
    const buildLocalFlowDraft = helpers.buildLocalFlowDraft || (() => ({ axes: [], nodes: [], connections: [] }));
    const buildInitialSessionData = helpers.buildInitialAgentSessionData || buildInitialAgentSessionData;
    const buildPlanSteps = helpers.buildAgentPlanSteps || buildAgentPlanSteps;

    const sessionSeed = buildInitialSessionData(userMessage, helpers) || null;
    const draft = sessionSeed?.draft || cloneJsonValue(buildLocalFlowDraft(userMessage));
    const assumptions = sessionSeed?.assumptions || [];
    const presetAnswers = sessionSeed?.presetAnswers || {};
    const plan = sessionSeed?.plan || buildPlanSteps().map((step, index) => ({ ...step, index }));

    return {
        ...createEmptySession(),
        active: true,
        status: "planning",
        jobStatus: "running",
        jobType: "flow_plan",
        canPoll: true,
        isTerminalJobState: false,
        stateAuthority: "client",
        goal: userMessage,
        sourceText: userMessage,
        plan,
        currentStepIndex: 0,
        assumptions,
        answers: { ...presetAnswers },
        draft,
        nextActions: sessionSeed?.nextActions || ["Parse input", "Draft flow"]
    };
}

export function createRestoredAgentSession(remote, persistedGoal = "", helpers = {}) {
    const createEmptySession = helpers.createEmptyAgentSession || createEmptyAgentSession;
    const buildPlanSteps = helpers.buildAgentPlanSteps || buildAgentPlanSteps;
    const buildAssumptions = helpers.buildAssumptionsFromAnswers || buildAssumptionsFromAnswers;
    const cloneJsonValue = helpers.cloneJsonValue || ((value) => JSON.parse(JSON.stringify(value)));
    const applyRemoteState = helpers.applyRemoteAgentSessionState || applyRemoteAgentSessionState;

    if (!remote?.sessionId) return null;

    const session = {
        ...createEmptySession(),
        active: true,
        mode: remote.mode || "flow",
        status: remote.status || "ready",
        jobStatus: remote.jobStatus || deriveAgentJobStatus(remote.status || "ready"),
        jobType: remote.jobType || "flow_plan",
        canPoll: remote.canPoll !== false,
        isTerminalJobState: Boolean(remote.isTerminalJobState),
        goal: remote.goal || persistedGoal || "",
        sourceText: remote.goal || persistedGoal || "",
        plan: (remote.mode || "flow") === "flow" ? buildPlanSteps().map((step, index) => ({ ...step, index })) : [],
        assumptions: buildAssumptions(remote.answers || {}),
        answers: { ...(remote.answers || {}) },
        draft: remote.draft ? cloneJsonValue(remote.draft) : null,
        generatedCode: String(remote.generatedCode || ""),
        remoteSessionId: remote.sessionId,
        remoteStatus: remote.status || "ready"
    };

    return typeof applyRemoteState === "function"
        ? applyRemoteState(session, remote, helpers)
        : session;
}

function parseSessionTimestamp(value) {
    const time = Date.parse(String(value || ""));
    return Number.isFinite(time) ? time : null;
}

export function shouldAcceptRemoteAgentSessionState(session, remote) {
    if (!session || !remote || typeof remote !== "object") return false;
    const localRevisionSource = session.stateAuthority === "server" ? session.serverRevisionCount : session.revisionCount;
    const localRevision = Number.isFinite(Number(localRevisionSource)) ? Number(localRevisionSource) : 0;
    const remoteRevision = Number.isFinite(Number(remote.revisionCount)) ? Number(remote.revisionCount) : 0;
    if (remoteRevision > localRevision) return true;
    if (remoteRevision < localRevision) return false;

    const localUpdatedAtSource = session.stateAuthority === "server" ? session.serverUpdatedAt : session.updatedAt;
    const localUpdatedAt = parseSessionTimestamp(localUpdatedAtSource);
    const remoteUpdatedAt = parseSessionTimestamp(remote.updatedAt);
    if (remoteUpdatedAt != null && localUpdatedAt != null) {
        if (remoteUpdatedAt > localUpdatedAt) return true;
        if (remoteUpdatedAt < localUpdatedAt) return false;
    }

    if (session.stateAuthority === "server" && session.clientRequestId && remote.clientRequestId) {
        return String(session.clientRequestId) === String(remote.clientRequestId);
    }
    return true;
}

export function applyAgentAnswerToSession(session, questionId, normalizedValue, helpers = {}) {
    const applyPlanAnswerToDraft = helpers.applyPlanAnswerToDraft || (() => {});
    if (!session || !questionId) return session;

    session.currentStepIndex = 4;
    session.status = "revising";
    session.jobStatus = "running";
    session.canPoll = true;
    session.isTerminalJobState = false;
    session.stateAuthority = "client";
    session.answers[questionId] = normalizedValue;
    applyPlanAnswerToDraft(session.draft, questionId, normalizedValue);
    session.revisionCount += 1;
    session.currentQuestionIndex = 0;
    return session;
}

export function prepareAgentSessionForInitialDraft(session) {
    if (!session) {
        return {
            session,
            agentStatus: "planning",
            activities: []
        };
    }

    session.currentStepIndex = 1;
    session.status = "acting";
    session.jobStatus = "running";
    session.canPoll = true;
    session.isTerminalJobState = false;
    return {
        session,
        agentStatus: "acting",
        activities: [
            { message: "Plan: Started a new agent planning session.", meta: "agent-plan" },
            { message: "Action: Built the first flow draft from the current request.", meta: "agent-action" }
        ]
    };
}

export function buildAgentDraftRefreshConfig(stage = "initial", hasApiGenerationClient = false) {
    if (stage === "revise") {
        return {
            chatStatus: hasApiGenerationClient ? "Updating plan with AI..." : "Updating plan locally...",
            remoteSuccessMessage: "Action: Loaded revised draft from the remote agent session.",
            aiSuccessMessage: "Action: Applied revised AI draft.",
            localFallbackMessage: "Observation: AI revise unavailable, keeping the local draft.",
            quotaFallbackMessage: "Observation: AI quota reached, keeping the local draft.",
            errorMessage: "Observation: Failed to revise with AI, keeping the local draft."
        };
    }

    return {
        chatStatus: hasApiGenerationClient ? "Requesting initial flow draft from AI..." : "Using local initial draft...",
        remoteSuccessMessage: "Action: Loaded draft from the remote agent session.",
        aiSuccessMessage: "Action: Applied the initial AI draft.",
        localFallbackMessage: "Observation: AI draft unavailable, keeping the local fallback draft.",
        quotaFallbackMessage: "Observation: AI quota reached, keeping the local fallback draft.",
        errorMessage: "Observation: Failed to fetch AI draft, using local fallback."
    };
}

export function buildAgentAnswerUiUpdate(questionId, normalizedValue, answerLabel = "", hasApiGenerationClient = false) {
    const displayValue = answerLabel || String(normalizedValue ?? "");
    return {
        agentStatus: "revising",
        answerText: displayValue,
        activity: {
            message: `Action: Applied answer for ${questionId}: ${String(normalizedValue)}`,
            meta: "agent-action"
        },
        confirmedFact: `${questionId}: ${String(normalizedValue)}`,
        refreshConfig: buildAgentDraftRefreshConfig("revise", hasApiGenerationClient)
    };
}

export function buildAgentQuestionReasonText(question) {
    if (!question) return "";
    if (question.id === "axis_name") return "Reason: axis is required to place motion nodes.";
    if (question.id === "off_branch") return "Reason: OFF branch behavior is still undefined.";
    if (question.id === "timeout_sec") return "Reason: timeout branch needs a timeout value.";
    if (question.id === "error_code") return "Reason: timeout error handling needs an error code.";
    if (question.id === "after_motion") return "Reason: final motion behavior is still undefined.";
    return "Reason: more information is needed to complete the flow.";
}

export function buildAgentQuestionUi(session, question, helpers = {}) {
    const buildReasonText = helpers.buildPlanReasonText || buildAgentQuestionReasonText;
    if (!session?.active || !question) return null;

    const prefix = `Question ${session.currentQuestionIndex + 1}/${session.questions.length}`;
    const reasonText = buildReasonText(question);
    const actions = [];
    if (Array.isArray(question.options)) {
        for (const option of question.options) {
            actions.push({ kind: "agent-answer", questionId: question.id, value: option.value, label: option.label });
        }
    }
    if (Array.isArray(question.quickOptions)) {
        for (const option of question.quickOptions) {
            actions.push({ kind: "agent-answer", questionId: question.id, value: option, label: option });
        }
    }

    session.currentStepIndex = 3;
    session.status = "asking";
    return {
        agentStatus: "asking",
        chatStatus: question.kind === "text" ? "Waiting for input" : "Waiting for selection",
        message: `${prefix}\n${reasonText}\n\nQuestion: ${question.text}`,
        meta: "agent-question",
        actions
    };
}

export function buildAgentFinalizeUi(finalizeResult) {
    const activeSession = finalizeResult?.session || null;
    if (!activeSession) return null;

    if (finalizeResult?.status === "failed") {
        activeSession.observations = finalizeResult.validationFindings || [];
        activeSession.questions = [];
        activeSession.currentQuestionIndex = 0;
        activeSession.nextActions = ["Review plan", "Adjust inputs and retry"];
        return {
            agentStatus: "failed",
            chatStatus: "Validation needs attention",
            message: "Validation found issues. Please review the plan before applying it.",
            meta: "agent-error",
            activities: (activeSession.observations || []).map((finding) => ({
                message: `Observation: ${finding.message}`,
                meta: "agent-observation"
            })),
            warnings: (activeSession.observations || []).map((finding) => finding.message)
        };
    }

    return {
        agentStatus: "ready",
        chatStatus: "Plan ready",
        message: "The plan is ready. You can apply it to the flow now.",
        meta: "agent-ready",
        activities: [
            {
                message: `Observation: Validation passed. ${activeSession.draft?.nodes?.length || 0} nodes are ready to apply.`,
                meta: "agent-observation"
            }
        ],
        actions: [
            { kind: "agent-apply", label: "Apply plan", variant: "primary" },
            { kind: "agent-cancel", label: "Review plan" }
        ]
    };
}

export function buildAgentLoopUi(activeSession, nextAction, options = {}) {
    const emitOverview = options.emitOverview !== false;
    const overviewText = emitOverview ? buildFlowPlanOverview(activeSession) : "";

    if (nextAction === "wait") {
        return {
            overviewText,
            agentStatus: activeSession?.status || "planning",
            chatStatus: "Server agent running",
            activities: [
                {
                    message: "Observation: Server-side agent is still processing the session.",
                    meta: "agent-observation"
                }
            ],
            shouldPresentQuestion: false,
            shouldFinalize: false
        };
    }

    if (nextAction === "failed") {
        const errors = (activeSession?.observations || []).filter((row) => row.severity === "error");
        return {
            overviewText,
            agentStatus: "failed",
            chatStatus: "Need corrections",
            message: "The draft still has issues, so I cannot proceed yet.",
            meta: "agent-error",
            activities: errors.map((observation) => ({
                message: `Observation: ${observation.message}`,
                meta: "agent-observation"
            })),
            warnings: errors.map((observation) => observation.message),
            shouldPresentQuestion: false,
            shouldFinalize: false
        };
    }

    if (nextAction === "ask_user") {
        return {
            overviewText,
            agentStatus: activeSession?.status || "asking",
            chatStatus: activeSession?.questions?.length ? "Waiting for input" : "",
            activities: [
                {
                    message: `Observation: ${activeSession?.questions?.length || 0} questions remain before planning can continue.`,
                    meta: "agent-observation"
                }
            ],
            shouldPresentQuestion: true,
            shouldFinalize: false
        };
    }

    return {
        overviewText,
        agentStatus: activeSession?.status || "observing",
        activities: [
            {
                message: "Observation: Required conditions are available, moving to validation.",
                meta: "agent-observation"
            }
        ],
        shouldPresentQuestion: false,
        shouldFinalize: true
    };
}

export function buildAgentRevisionInstruction(session) {
    const lines = [String(session?.goal || session?.sourceText || "フローを作成").trim()];
    const answers = session?.answers || {};
    if (answers.axis_name) lines.push(`使用軸: ${answers.axis_name}`);
    if (answers.off_branch) lines.push(`OFF分岐: ${answers.off_branch}`);
    if (answers.timeout_sec) lines.push(`タイムアウト秒: ${answers.timeout_sec}`);
    if (answers.error_code) lines.push(`エラーコード: ${answers.error_code}`);
    if (answers.after_motion) lines.push(`最後の動作後: ${answers.after_motion}`);
    return lines.filter(Boolean).join("\n");
}

export function buildAssumptionsFromAnswers(answers = {}) {
    const rows = [];
    if (answers.axis_name) rows.push(`使用軸は ${answers.axis_name} と仮定`);
    if (answers.timeout_sec) rows.push(`タイムアウトは ${answers.timeout_sec} 秒`);
    if (answers.error_code) rows.push(`タイムアウト時エラーコードは ${answers.error_code}`);
    if (answers.off_branch) rows.push(`OFF 分岐は ${answers.off_branch}`);
    if (answers.after_motion) rows.push(`最後の動作後は ${answers.after_motion}`);
    return rows;
}

export function buildFlowPlanOverview(session) {
    const axisNames = (session.draft?.axes || []).map((axis) => axis.name).join(", ") || "未設定";
    const nodeCount = (session.draft?.nodes || []).length;
    const unresolvedQuestions = (session.questions || []).filter((question) => session.answers?.[question.id] == null).length;
    const lines = [
        "エージェントセッションを更新しました。",
        `状態: ${session.status || "idle"}`,
        `対象軸: ${axisNames}`,
        `ノード候補: ${nodeCount} 件`,
        `未確定: ${unresolvedQuestions} 件`
    ];
    if (session.plan.length) {
        lines.push("", "現在のプラン:");
        for (const step of session.plan) {
            const mark = step.index === session.currentStepIndex ? ">" : "-";
            lines.push(`${mark} ${step.label}`);
        }
    }
    if (session.observations.length) {
        lines.push("", "観測:");
        for (const row of session.observations) lines.push(`- [${row.severity}] ${row.message}`);
    }
    if (session.assumptions.length) {
        lines.push("", "仮定:");
        for (const row of session.assumptions) lines.push(`- ${row}`);
    }
    if (session.memory?.confirmedFacts?.length) {
        lines.push("", "確定事項:");
        for (const row of session.memory.confirmedFacts) lines.push(`- ${row}`);
    }
    if (session.nextActions.length) {
        lines.push("", "次の候補:");
        for (const row of session.nextActions) lines.push(`- ${row}`);
    }
    return lines.join("\n");
}

export function applyRemoteAgentSessionState(session, remote, helpers = {}) {
    const cloneJsonValue = helpers.cloneJsonValue || ((value) => JSON.parse(JSON.stringify(value)));
    const buildAssumptions = helpers.buildAssumptionsFromAnswers || buildAssumptionsFromAnswers;
    const shouldAcceptRemoteState = helpers.shouldAcceptRemoteAgentSessionState || shouldAcceptRemoteAgentSessionState;
    if (!session || !remote) return session;
    if (typeof shouldAcceptRemoteState === "function" && !shouldAcceptRemoteState(session, remote)) {
        return session;
    }

    if (typeof remote.goal === "string" && remote.goal) {
        session.goal = remote.goal;
        session.sourceText = remote.goal;
    }
    if (typeof remote.mode === "string" && remote.mode) {
        session.mode = remote.mode;
    }
    if (remote.answers && typeof remote.answers === "object") {
        session.answers = { ...remote.answers };
    }
    if (Array.isArray(remote.assumptions)) {
        session.assumptions = [...remote.assumptions];
    } else {
        session.assumptions = buildAssumptions(session.answers || {});
    }
    if (remote.draft) {
        session.draft = cloneJsonValue(remote.draft);
    }
    if (typeof remote.generatedCode === "string") {
        session.generatedCode = remote.generatedCode;
    }
    if (Array.isArray(remote.observations)) {
        session.observations = cloneJsonValue(remote.observations);
    }
    if (Array.isArray(remote.questions)) {
        session.questions = cloneJsonValue(remote.questions);
    }
    if (Array.isArray(remote.nextActions)) {
        session.nextActions = [...remote.nextActions];
    }
    if (typeof remote.nextAction === "string") {
        session.nextAction = remote.nextAction;
    }
    if (remote.memory && typeof remote.memory === "object") {
        session.memory = {
            assumptions: Array.isArray(remote.memory.assumptions) ? [...remote.memory.assumptions] : [],
            rejectedIdeas: Array.isArray(remote.memory.rejectedIdeas) ? [...remote.memory.rejectedIdeas] : [],
            confirmedFacts: Array.isArray(remote.memory.confirmedFacts) ? [...remote.memory.confirmedFacts] : [],
            warnings: Array.isArray(remote.memory.warnings) ? [...remote.memory.warnings] : []
        };
    }
    if (typeof remote.status === "string" && remote.status) {
        session.status = remote.status;
        session.remoteStatus = remote.status;
    }
    if (typeof remote.jobStatus === "string" && remote.jobStatus) {
        session.jobStatus = remote.jobStatus;
    } else {
        session.jobStatus = deriveAgentJobStatus(session.status);
    }
    if (typeof remote.jobType === "string" && remote.jobType) {
        session.jobType = remote.jobType;
    }
    session.canPoll = remote.canPoll !== false;
    session.isTerminalJobState = Boolean(remote.isTerminalJobState);
    if (typeof remote.workspaceId === "string") {
        session.workspaceId = remote.workspaceId;
    }
    if (typeof remote.projectId === "string") {
        session.projectId = remote.projectId;
    }
    if (typeof remote.generationRunId === "string") {
        session.generationRunId = remote.generationRunId;
    }
    if (typeof remote.clientRequestId === "string") {
        session.clientRequestId = remote.clientRequestId;
    }
    if (typeof remote.etag === "string" && remote.etag) {
        session.remoteEtag = remote.etag;
    }
    session.generationRunPersisted = Boolean(remote.generationRunPersisted);
    session.stateAuthority = "server";
    if (remote.sessionId) {
        session.remoteSessionId = remote.sessionId;
    }
    if (Number.isFinite(Number(remote.currentStepIndex))) {
        session.currentStepIndex = Number(remote.currentStepIndex);
    }
    if (Number.isFinite(Number(remote.revisionCount))) {
        session.revisionCount = Number(remote.revisionCount);
        session.serverRevisionCount = Number(remote.revisionCount);
    }
    if (typeof remote.updatedAt === "string") {
        session.updatedAt = remote.updatedAt;
        session.serverUpdatedAt = remote.updatedAt;
    }
    if (typeof remote.localUpdatedAt === "string" && remote.localUpdatedAt) {
        session.localUpdatedAt = remote.localUpdatedAt;
    }
    if (typeof remote.createdAt === "string" && remote.createdAt) {
        session.createdAt = remote.createdAt;
    }
    return session;
}

export function clearPersistedAgentSessionReference(storage = globalThis?.localStorage) {
    try {
        storage?.removeItem(AGENT_SESSION_STORAGE_KEY);
    } catch (error) {
        console.warn("agent session ref clear failed", error);
    }
}

export function normalizePersistedAgentSessionReference(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const remoteSessionId = typeof value.remoteSessionId === "string" ? value.remoteSessionId.trim() : "";
    if (!remoteSessionId) return null;
    const mode = typeof value.mode === "string" && value.mode ? value.mode : "flow";
    return {
        version: Number.isFinite(Number(value.version))
            ? Number(value.version)
            : AGENT_SESSION_REFERENCE_VERSION,
        remoteSessionId,
        goal: typeof value.goal === "string" ? value.goal : "",
        mode,
        updatedAt: typeof value.updatedAt === "string" ? value.updatedAt : "",
        serverUpdatedAt: typeof value.serverUpdatedAt === "string" ? value.serverUpdatedAt : "",
        revisionCount: Number.isFinite(Number(value.revisionCount)) ? Number(value.revisionCount) : 0,
        persistedAt: typeof value.persistedAt === "string" ? value.persistedAt : ""
    };
}

export function readPersistedAgentSessionReference(storage = globalThis?.localStorage) {
    try {
        const raw = storage?.getItem(AGENT_SESSION_STORAGE_KEY) || "";
        if (!raw) return null;
        return normalizePersistedAgentSessionReference(JSON.parse(raw));
    } catch (error) {
        console.warn("agent session ref load failed", error);
        return null;
    }
}

export function persistAgentSessionReference(session, storage = globalThis?.localStorage) {
    try {
        if (
            !session?.active
            || !session.remoteSessionId
            || session.isTerminalJobState
        ) {
            clearPersistedAgentSessionReference(storage);
            return;
        }
        const mode = typeof session.mode === "string" && session.mode ? session.mode : "flow";
        const serverUpdatedAt = session.serverUpdatedAt || session.updatedAt || "";
        storage?.setItem(AGENT_SESSION_STORAGE_KEY, JSON.stringify({
            version: AGENT_SESSION_REFERENCE_VERSION,
            remoteSessionId: session.remoteSessionId,
            goal: session.goal || session.sourceText || "",
            mode,
            updatedAt: session.updatedAt || new Date().toISOString(),
            serverUpdatedAt,
            revisionCount: Number.isFinite(Number(session.serverRevisionCount))
                ? Number(session.serverRevisionCount)
                : Number.isFinite(Number(session.revisionCount))
                    ? Number(session.revisionCount)
                    : 0,
            persistedAt: new Date().toISOString()
        }));
    } catch (error) {
        console.warn("agent session ref save failed", error);
    }
}
